import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtEUR } from "@/lib/api";
import { CheckCircle2, Copy, FileText, Truck, Mail } from "lucide-react";
import { toast } from "sonner";

export default function OrderSuccess() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        api.get(`/orders/${id}`).then((r) => setOrder(r.data));
    }, [id]);

    if (!order) return <div className="container-edge py-20 font-mono">A carregar…</div>;

    const ref = order.payment_reference || {};
    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copiado!");
    };

    return (
        <div className="container-edge py-12 max-w-3xl" data-testid="order-success-page">
            <div className="border-2 border-ink bg-white p-8">
                <div className="flex items-center gap-3 text-accent-green">
                    <CheckCircle2 size={32} />
                    <div className="label-mono text-accent-green">Encomenda Confirmada</div>
                </div>
                <h1 className="font-display font-black text-4xl tracking-tighter mt-3" data-testid="order-number">
                    {order.order_number}
                </h1>
                <p className="text-ink/70 mt-2">
                    Obrigado, <strong>{order.customer.name}</strong>! Recebemos a sua encomenda.
                    Enviamos a confirmação para <strong>{order.customer.email}</strong>.
                </p>
            </div>

            {/* Multibanco block */}
            {order.payment_method === "multibanco" && (
                <section className="mt-6 border-2 border-magenta bg-magenta/5 p-8" data-testid="mb-reference-block">
                    <div className="label-mono text-magenta">Referência Multibanco</div>
                    <div className="mt-1 text-sm text-ink/70">
                        Pague via homebanking ou ATM até{" "}
                        {ref.expires_at
                            ? new Date(ref.expires_at).toLocaleDateString("pt-PT")
                            : "—"}
                        .
                    </div>
                    <div className="mt-6 grid sm:grid-cols-3 gap-4 font-mono">
                        <RefBlock label="Entidade" value={ref.entity} onCopy={() => copy(ref.entity)} />
                        <RefBlock label="Referência" value={ref.reference} onCopy={() => copy(ref.reference)} />
                        <RefBlock label="Montante" value={fmtEUR(ref.amount)} onCopy={() => copy(String(ref.amount))} />
                    </div>
                    {ref.sandbox && (
                        <div className="mt-4 text-xs font-mono text-ink/60">
                            ⚠️ Modo SANDBOX — referência simulada para testes.
                        </div>
                    )}
                </section>
            )}

            {order.payment_method === "mbway" && (
                <section className="mt-6 border-2 border-magenta bg-magenta/5 p-8" data-testid="mbway-block">
                    <div className="label-mono text-magenta">Pagamento MB Way</div>
                    <p className="mt-2">
                        Pedido de pagamento enviado para <strong>{ref.phone}</strong>. Aprove
                        a notificação na app MB Way.
                    </p>
                    <div className="mt-3 font-mono text-sm">
                        ID: {ref.request_id} · {fmtEUR(ref.amount)}
                    </div>
                </section>
            )}

            {order.payment_method === "ccard" && (
                <section className="mt-6 border-2 border-magenta bg-magenta/5 p-8">
                    <div className="label-mono text-magenta">Pagamento por cartão</div>
                    <p className="mt-2">
                        O pagamento será confirmado em instantes. ID transação: <span className="font-mono">{ref.transaction_id}</span>
                    </p>
                </section>
            )}

            {/* Items */}
            <section className="mt-6 border-2 border-ink bg-white">
                <div className="p-6 border-b-2 border-ink">
                    <h2 className="font-display font-black text-xl">Detalhes da encomenda</h2>
                </div>
                <div className="divide-y-2 divide-ink">
                    {order.items.map((it) => (
                        <div key={it.sku} className="p-5 flex items-center justify-between">
                            <div>
                                <div className="font-bold">{it.name}</div>
                                <div className="text-xs font-mono text-ink/60">
                                    SKU: {it.sku} · {it.quantity} × {fmtEUR(it.price)}
                                </div>
                            </div>
                            <div className="font-mono">{fmtEUR(it.line_total)}</div>
                        </div>
                    ))}
                </div>
                <div className="p-5 border-t-2 border-ink space-y-1 font-mono text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{fmtEUR(order.subtotal)}</span></div>
                    <div className="flex justify-between"><span>Portes ({order.shipping_zone_name})</span><span>{fmtEUR(order.shipping_cost)}</span></div>
                    <div className="flex justify-between text-ink/60"><span>IVA incluído (23%)</span><span>{fmtEUR(order.tax)}</span></div>
                    <div className="flex justify-between font-display font-black text-xl pt-3 border-t-2 border-ink mt-3">
                        <span>Total</span><span>{fmtEUR(order.total)}</span>
                    </div>
                </div>
            </section>

            <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <InfoCard icon={Truck} t="Transporte" s={`${order.shipping_carrier} · ${order.shipping_zone_name}`} />
                <InfoCard icon={FileText} t="Fatura Moloni" s="Gerada após confirmação do pagamento" />
                <InfoCard icon={Mail} t="Notificações" s={order.customer.email} />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/loja" className="btn-secondary">Continuar a comprar</Link>
                <Link to="/" className="btn-ghost">← Voltar ao início</Link>
            </div>
        </div>
    );
}

function RefBlock({ label, value, onCopy }) {
    return (
        <div className="border-2 border-ink bg-white p-4">
            <div className="label-mono">{label}</div>
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xl font-bold tracking-wider">{value}</span>
                <button onClick={onCopy} className="text-ink/60 hover:text-magenta">
                    <Copy size={14} />
                </button>
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, t, s }) {
    return (
        <div className="border-2 border-ink bg-white p-4">
            <Icon className="text-magenta" size={20} />
            <div className="font-bold uppercase text-sm mt-2">{t}</div>
            <div className="text-xs font-mono text-ink/60 mt-1 break-words">{s}</div>
        </div>
    );
}
